// @flow
import React from 'react';
import Box from 'sail/Box';
import Group from 'sail/Group';
import {ButtonLink} from 'sail/Button';
import {BodyAlt} from 'sail/Text';
import Icon from 'sail/Icon';
import {useNavigation, useLocation, useRandom, useAccount} from 'docs/data';
import {useLocationChangeHandler} from 'docs/hooks';
import {getLoginUrl} from 'docs/util';
import type {
  RandomStringFunction,
  NavigationNode,
  NavigationTopic,
  NavigationProduct,
} from 'docs/data';
import {FormattedMessage} from 'react-intl';
import {headerMessages} from './Header.messages';

import './MobileNav.scss';

/**
 * Walks our list of navigation nodes and if headings exists
 * groups nodes that come after a heading as children of
 * that heading
 * @param {Array<NavigationNode>} nodes
 */
const groupByHeadings = (nodes: Array<NavigationNode>) => {
  const freeNodes = [];
  const headings = [];
  for (const node of nodes) {
    if (node.type === 'heading') {
      headings.push({...node, children: []});
    } else if (headings.length) {
      headings[headings.length - 1].children.push(node);
    } else {
      freeNodes.push(node);
    }
  }
  return [...freeNodes, ...headings];
};

/**
 * Removes duplicate nodes but maintains heading nodes
 * while preserving order
 * @param {Array<NavigationNode>} nodes
 */
const deDupeNodes = (allNodes: Array<NavigationNode>) => {
  return allNodes.reduce(
    ({deDupedNodes, seen}, node) => {
      if (node.type === 'heading') {
        deDupedNodes.push(node);
      } else if (!seen.has(node.href)) {
        deDupedNodes.push(node);
        seen.add(node.href);
      }
      return {deDupedNodes, seen};
    },
    {deDupedNodes: [], seen: new Set()},
  ).deDupedNodes;
};

type MobileNavOptionData = {|
  label: string,
  value: string,
  key: string,
  heading?: boolean,
  disabled?: boolean,
  children?: Array<MobileNavOptionData>,
|};

/**
 * Searches a collection of nested nav nodes for a navigation link
 * that matches the provided href string
 * @param {Array<MobileNavOptionData>} options
 * @param {string} href
 */
const findSelectedOption = (
  options: Array<MobileNavOptionData>,
  href: string,
): ?MobileNavOptionData => {
  for (const opt of options) {
    if (!opt.heading && opt.value === href) {
      return opt;
    }
    let foundOpt;
    if (opt.children) {
      foundOpt = findSelectedOption(opt.children, href);
    }
    if (foundOpt) {
      return foundOpt;
    }
  }
};

/**
 * Creates options array for top level navigation
 * @param {Array<NavigationTopic>} topics
 */
const getMobileNavOptionsForTopLevelPage = (
  topics: Array<NavigationTopic>,
): Array<MobileNavOptionData> => {
  return topics.map((node) => ({
    label: node.text,
    value: node.href,
    key: node.href,
  }));
};

/**
 * Creates options array for when the user is on a
 * page that is nested within a product sidenav
 * @param {Array<NavigationProduct>} products
 * @param {?NavigationProduct} activeProduct
 * @param {string} currentHref
 * @param {RandomStringFunction} generateRandomString
 */
const getMobileNavOptionsForInnerPage = (
  products: Array<NavigationProduct>,
  activeProduct: ?NavigationProduct,
  currentHref: string,
  generateRandomString: RandomStringFunction,
): Array<MobileNavOptionData> => {
  // if we don't have an activeProduct, just fall back
  // to the list of products. this will happen when, for
  // example, the user is on /docs/payments
  const navItems = activeProduct
    ? [activeProduct, ...activeProduct.children]
    : [...products];

  // put the topic at the beginning of our list of
  // navigation nodes so the user has the ability to
  // navigate "up" the tree without having to expose
  // the entire top level
  if (activeProduct && activeProduct.topic) {
    navItems.unshift(activeProduct.topic);
  }
  // Always give the user a way back to home
  navItems.unshift({
    text: 'Docs home',
    href: '/docs',
    translated: null,
  });

  const convertNodeToOption = (node) => {
    if (node.type === 'heading') {
      return {
        label: node.text,
        value: '',
        key: generateRandomString(16),
        heading: true,
        children: node.children?.map(convertNodeToOption),
      };
    }
    if (node.type === 'link' && node.expanded) {
      return {
        label: node.text,
        value: node.href,
        key: node.href,
        children: node.children?.map(convertNodeToOption),
      };
    }
    return {
      label: node.text,
      value: node.href,
      key: node.href,
    };
  };
  // nodes are a flat list, with "headings" interspersed with actual
  // navigation link nodes. we want to create actual groupings so that
  // we can use make use of <optgroup> in our select input
  // $FlowFixMe flow is sad because we unshift plain objects onto navItems
  const nodes = groupByHeadings(deDupeNodes(navItems));
  const options = nodes.map(convertNodeToOption);
  return options;
};

const MobileNavOption = ({opt}) => {
  return (
    <option value={opt.value} disabled={opt.disabled}>
      {opt.label}
    </option>
  );
};

export const MobileNavRightLink = () => {
  const {account} = useAccount();

  return (
    <Box
      padding={{left: 32, right: 8}}
      flex={{alignItems: 'center'}}
      className="Header-buttons--small"
    >
      <Group direction="horizontal" spacing={8} alignItems="center">
        <BodyAlt>
          <ButtonLink
            color="gray700"
            label={
              account.user ? (
                <FormattedMessage {...headerMessages.dashboard} />
              ) : (
                <FormattedMessage {...headerMessages.signIn} />
              )
            }
            href={getLoginUrl({account})}
          />
        </BodyAlt>
        <Icon size={12} icon="arrowRight" />
      </Group>
    </Box>
  );
};

export const MobileNav = () => {
  const {location} = useLocation();
  const {navigation} = useNavigation();
  const {generateRandomString} = useRandom();
  const {topics, products} = navigation;

  const activeTopic = topics.find((t) => t.active);
  const activeProduct = products.find((t) => t.active);

  const isOnTopicPage = !!(
    activeTopic && activeTopic.href === location.pathname
  );
  const isOrphanedPage = !activeTopic && !activeProduct;

  const options =
    isOnTopicPage || isOrphanedPage
      ? getMobileNavOptionsForTopLevelPage(topics)
      : getMobileNavOptionsForInnerPage(
          products,
          activeProduct,
          location.pathname,
          generateRandomString,
        );

  const changeHandler = useLocationChangeHandler();

  const renderOption = (opt, level = 0) => {
    if (opt.heading) {
      return (
        <optgroup label={opt.label} key={opt.label}>
          {(opt.children || []).map((o) => renderOption(o, level))}
        </optgroup>
      );
    }
    // Prepend the label with hyphens to simulate indentation
    let prefix = '';
    if (level > 0) {
      prefix = new Array(level).fill('-').join('');
      prefix += ' ';
    }
    const optWithPrefixedLabel = {
      ...opt,
      label: prefix + opt.label,
    };
    if (optWithPrefixedLabel.children?.length) {
      return (
        <React.Fragment key={generateRandomString(16)}>
          <MobileNavOption
            opt={optWithPrefixedLabel}
            key={optWithPrefixedLabel.key}
          />
          {optWithPrefixedLabel.children?.map((o) =>
            renderOption(o, level + 1),
          )}
        </React.Fragment>
      );
    }
    return (
      <MobileNavOption
        opt={optWithPrefixedLabel}
        key={optWithPrefixedLabel.key}
      />
    );
  };

  const selected = findSelectedOption(options, location.pathname);

  return (
    <Box className="MobileNav">
      <label>{selected?.label || 'Navigation'}</label>
      <Icon icon="chevronDown" size={12} className="MobileNav-arrow" />
      <select
        value={location.pathname}
        onChange={(ev) => {
          changeHandler(ev.target.value);
        }}
      >
        {options.map((o) => renderOption(o))}
      </select>
    </Box>
  );
};
